import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useGoogleFont } from '@/features/customization/hooks/useGoogleFont';
import {
  parseCustomization,
  toBrandCssVars,
} from '@/features/customization/schemas/customizationSchemas';
import { publicSessionKeys, usePublicSession } from '../api/usePublicSession';
import { MobileOnlyGate } from '../components/MobileOnlyGate';
import { SessionMobilePanel } from '../components/SessionMobilePanel';
import { useNoZoomViewport } from '../hooks/useNoZoomViewport';
import { PublicRunContext, type PublicRunContextValue } from '../publicRun';
import type { SessionDetailDto } from '../schemas/sessionSchemas';

/**
 * Public applicant run — mounted OUTSIDE the authenticated shell at /w/:token.
 * An anonymous applicant opens their shared link and runs the workflow exactly
 * like the internal fill flow (same steps, rules, branding), but every API call
 * goes through the token-authorized public endpoints (see PublicRunContext →
 * useVerifyStep). No login, no tenant.
 */
export function PublicSessionRunScreen() {
  // App-like: no pinch / double-tap zoom while the applicant is in the run.
  useNoZoomViewport();

  const { token } = useParams<{ token: string }>();
  const qc = useQueryClient();
  const { data: session, isLoading, isError } = usePublicSession(token);

  const branding = parseCustomization(session?.customization?.customizationJson);
  const brandVars = toBrandCssVars(branding);
  useGoogleFont(session?.customization ? branding.typographyStyle : undefined);

  const runCtx = useMemo<PublicRunContextValue | null>(() => {
    if (!token) return null;
    return {
      token,
      onSessionUpdate: (updated: SessionDetailDto) =>
        qc.setQueryData(publicSessionKeys.detail(token), updated),
    };
  }, [token, qc]);

  return (
    // `sc-session-preview-page` owns the whole frame (see styles/globals.css): a
    // fixed 100dvh, white on a phone with the tinted page returning only from
    // 481px up, plus safe-area insets. It replaces the `min-h-dvh bg-[#F1F5FB]`
    // that used to be here — that painted a tinted layer the panel then painted
    // again, which is what produced the blue bands around a white sheet and let
    // the whole document scroll.
    <div className="sc-session-preview-page brand-scope relative" style={brandVars}>
      <div className="flex min-h-0 w-full flex-1 flex-col">
        {session && runCtx ? (
          // Workflow Settings → "Allow desktop access". Gated here rather than in
          // SessionMobilePanel so the whole run — every step — is covered by one check.
          <MobileOnlyGate allowDesktop={session.options?.allowDesktopAccess ?? true}>
            <PublicRunContext.Provider value={runCtx}>
              <SessionMobilePanel session={session} mode="fill" />
            </PublicRunContext.Provider>
          </MobileOnlyGate>
        ) : (
          <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-[#666666]">
            {isLoading
              ? 'Loading your verification…'
              : isError
                ? 'This link is invalid or has expired.'
                : 'Verification not found.'}
          </div>
        )}
      </div>
    </div>
  );
}
